import type { User } from "../types";

/**
 * Small copies of data kept in localStorage so the app can show something
 * immediately at start (and on a bad connection). Firestore itself only
 * caches in memory now (see firebase.ts), so these replace its disk cache for
 * the two things the app waits on: the signed-in user's profile and the users
 * list staff/chat screens show.
 */

const PROFILE_PREFIX = "nn-profile-";
const USERS_PREFIX = "nn-users-";

// Enough to show a user in lists and chats; the full documents (training and
// meal plans) are too big and are always loaded from the server.
const USER_SUMMARY_FIELDS: (keyof User)[] = [
  "id",
  "name",
  "email",
  "phone",
  "role",
  "avatar",
  "trainedBy",
  "subscriptionEndDate",
  "subscriptionPause",
  "createdAt",
  "lastLogin",
  "lastActiveAt",
  "highlightColor",
  "paymentPlan",
  "secondInstallmentPaidAt",
  "badges",
  "profileSetupCompleted",
];

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

// Firebase keeps the login in the same storage (about 5 MB per site), so a
// copy that would take a big share of it is not kept at all.
const MAX_SNAPSHOT_CHARS = 1_000_000;

function write(key: string, value: unknown) {
  try {
    const json = JSON.stringify(value);
    if (json.length > MAX_SNAPSHOT_CHARS) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, json);
  } catch {
    // Storage full or unavailable: the copy is only a convenience.
  }
}

export function saveProfileSnapshot(user: User) {
  write(PROFILE_PREFIX + user.id, user);
}

export function loadProfileSnapshot(uid: string): User | null {
  const user = read<User>(PROFILE_PREFIX + uid);
  return user && user.id === uid ? user : null;
}

// Contact and payment details are only kept for staff (who work with them); other users'
// devices don't need everyone's email and phone number.
const CONTACT_FIELDS = new Set<keyof User>([
  "email",
  "phone",
  "paymentPlan",
  "secondInstallmentPaidAt",
]);

export function saveUsersSnapshot(ownerUid: string, users: Iterable<User>) {
  const list = Array.from(users);
  const owner = list.find((u) => u.id === ownerUid);
  const keepContacts = !!owner && owner.role !== "user";
  const summaries = list.map((u) => {
    const summary: Partial<User> = {};
    for (const field of USER_SUMMARY_FIELDS) {
      if (!keepContacts && CONTACT_FIELDS.has(field) && u.id !== ownerUid) continue;
      if (u[field] !== undefined) (summary as Record<string, unknown>)[field] = u[field];
    }
    return summary;
  });
  write(USERS_PREFIX + ownerUid, summaries);
}

export function loadUsersSnapshot(ownerUid: string): User[] | null {
  const users = read<User[]>(USERS_PREFIX + ownerUid);
  return Array.isArray(users) && users.length > 0 ? users : null;
}

/** Removes every copy (on logout, so the next account can't see them). */
export function clearLocalSnapshots() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith(PROFILE_PREFIX) || key.startsWith(USERS_PREFIX))) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing to clear.
  }
}

/**
 * The uid of the account Firebase Auth has saved on this device, read
 * directly from its localStorage entry. Firebase only reports the signed-in
 * user after checking the account with its server (a network round trip on
 * every start), so this lets the app start before that finishes.
 */
export function loadSignedInUidHint(): string | null {
  const auth = read<{ uid?: string }>(
    `firebase:authUser:${import.meta.env.VITE_FIREBASE_API_KEY}:[DEFAULT]`,
  );
  return typeof auth?.uid === "string" ? auth.uid : null;
}
